import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/AppLayout"
import { Head, useForm } from "@inertiajs/react"
import { IconArrowLeft, IconInfoCircle, IconSparkles } from "@tabler/icons-react"
import { useRef } from "react"

export default function BillImport() {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { data, setData, post, processing } = useForm<{
    csv_file: File | null
  }>({
    csv_file: null,
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post("/bills/process_import", {
      forceFormData: true,
    })
  }

  return (
    <>
      <Head title="Import Bills - Operations Center" />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/bills">Bills</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Import</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href="/bills">
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <h1 className="text-lg font-semibold tracking-tight">Import Bills from CSV</h1>
          </div>
        </div>

        <div className="max-w-2xl">
          <form onSubmit={handleSubmit}>
            <Card>
              <CardHeader>
                <CardTitle>Upload CSV</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="csv_file">Select CSV File</Label>
                  <input
                    ref={fileInputRef}
                    id="csv_file"
                    type="file"
                    accept=".csv"
                    required
                    onChange={(e) => setData("csv_file", e.target.files?.[0] || null)}
                    className="border-input bg-background block w-full rounded-md border px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
                  />
                </div>

                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/30">
                  <div className="flex gap-3">
                    <IconInfoCircle className="mt-0.5 h-5 w-5 shrink-0 text-blue-500" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-800 dark:text-blue-300">CSV Format Requirements</p>
                      <div className="mt-2 space-y-2 text-blue-700 dark:text-blue-400">
                        <p>Your CSV file should have the following columns with headers:</p>
                        <ul className="list-inside list-disc space-y-1">
                          <li>
                            <strong>Task Name</strong> - Vendor/supplier name (required)
                          </li>
                          <li>
                            <strong>Value (currency)</strong> - Amount paid (required)
                          </li>
                          <li>
                            <strong>Currency (drop down)</strong> - Currency code (USD, UYU, etc.)
                          </li>
                          <li>
                            <strong>Date paid (date)</strong> - Payment date
                          </li>
                          <li>
                            <strong>Category (drop down)</strong> - Category (Utilities, Marketing, etc.)
                          </li>
                          <li>
                            <strong>Payment type (drop down)</strong> - One-Time, Monthly, etc. (optional)
                          </li>
                        </ul>

                        <p className="pt-2 font-medium">Example CSV:</p>
                        <pre className="overflow-x-auto rounded border border-blue-200 bg-white p-2 font-mono text-xs dark:border-blue-800 dark:bg-gray-900">
                          {`"Task Name","Value  (currency)","Currency (drop down)","Date paid (date)","Category (drop down)","Payment type (drop down)"
"Google Workspaces",54,"USD","Friday, March 3rd 2023","Utilities","Monthly"
"Domain Registration",4.48,"USD","Tuesday, February 21st 2023","Miscellaneous","One-Time"`}
                        </pre>

                        <p className="pt-2">
                          <strong>Supported Categories:</strong> Miscellaneous, Utilities, Marketing, Legal, Equipment,
                          Salary, BPS, FONASA, Supplier, Office, Software, Hardware, Services, Supplies, Others
                        </p>
                      </div>

                      <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-900 dark:bg-green-950/30">
                        <div className="flex items-start gap-2">
                          <IconSparkles className="mt-0.5 h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />
                          <div>
                            <p className="font-medium text-green-800 dark:text-green-300">Automatic User Matching</p>
                            <p className="mt-1 text-xs text-green-700 dark:text-green-400">
                              The importer automatically detects and matches users from the Task Name:
                            </p>
                            <ul className="mt-2 list-inside list-disc space-y-1 text-xs text-green-700 dark:text-green-400">
                              <li>
                                <strong>&quot;Sueldo Alex&quot;</strong> matches &quot;Alexander Yarigin&quot;
                                (nickname)
                              </li>
                              <li>
                                <strong>&quot;Payment Manuel&quot;</strong> matches &quot;Manuel Morandi&quot; (first
                                name)
                              </li>
                              <li>
                                <strong>&quot;Slack Pro&quot;</strong> - no match found, bill left without user
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="justify-end gap-2 border-t">
                <Button variant="ghost" asChild>
                  <a href="/bills">Cancel</a>
                </Button>
                <Button type="submit" disabled={processing || !data.csv_file}>
                  {processing ? "Importing..." : "Import CSV"}
                </Button>
              </CardFooter>
            </Card>
          </form>
        </div>
      </div>
    </>
  )
}

BillImport.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
